import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Skeleton,
  Typography,
  Select,
  MenuItem,
  Switch,
  Tooltip,
  TablePagination,
  Checkbox,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

const PassengerCitiesTable = ({
  items = [],
  rowCount = 0,
  page = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  searchValue = "",
  onSearchChange,
  onSearchSubmit,
  isLoading = false,
  canUpdate = false,
  canDelete = false,
  onEdit,
  onDelete,
  onToggleStatus,
  onBulkDelete,
}) => {
  const { t } = useTranslation("products");
  const [selected, setSelected] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const isRowSelected = (id) => selected.has(id);
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onPageIds = items.map((r) => r.id);
  const allOnPageSelected = items.length > 0 && items.every((r) => selected.has(r.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) onPageIds.forEach((id) => next.delete(id));
      else onPageIds.forEach((id) => next.add(id));
      return next;
    });

  const selectedArr = Array.from(selected);
  const showActions = canUpdate || canDelete;
  const colSpan = 5 + (showActions ? 1 : 0) + (canDelete ? 1 : 0);

  return (
    <Paper variant="outlined">
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder={t("passengerCitiesPage.searchPlaceholder")}
          value={searchValue}
          onChange={(e) => onSearchChange?.(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearchSubmit?.(searchValue)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {canDelete && selectedArr.length > 0 && (
        <Box
          sx={{
            mx: 1.5,
            mb: 2,
            py: 1,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            {t("passengerCitiesPage.selectedCount", { count: selectedArr.length })}
          </Typography>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => setConfirmBulkDelete(true)}
            sx={{ textTransform: "uppercase" }}
          >
            {t("passengerCitiesPage.deleteSelected", { count: selectedArr.length })}
          </Button>
        </Box>
      )}

      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}>
        <DialogTitle>{t("passengerCitiesPage.deleteSelectedTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("passengerCitiesPage.deleteSelectedConfirm", { count: selectedArr.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)}>
            {t("passengerCitiesPage.cancel")}
          </Button>
          <Button
            color="error"
            onClick={() => {
              onBulkDelete?.(selectedArr);
              setSelected(new Set());
              setConfirmBulkDelete(false);
            }}
          >
            {t("passengerCitiesPage.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canDelete ? (
                <TableCell width={48}>
                  <Checkbox
                    checked={allOnPageSelected}
                    indeterminate={!allOnPageSelected && selectedArr.length > 0}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
              ) : null}
              <TableCell>{t("passengerCitiesPage.colNome")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colCodigo")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colUf")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colFonte")}</TableCell>
              <TableCell>{t("passengerCitiesPage.colStatus")}</TableCell>
              {showActions ? (
                <TableCell align="right">{t("passengerCitiesPage.colActions")}</TableCell>
              ) : null}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <TableRow key={row}>
                    {canDelete ? (
                      <TableCell>
                        <Skeleton variant="rectangular" width={20} height={20} />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <Skeleton variant="text" width="80%" height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={40} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={64} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={96} height={20} />
                    </TableCell>
                    {showActions ? (
                      <TableCell align="right">
                        <Skeleton
                          variant="rounded"
                          width={72}
                          height={28}
                          sx={{ ml: "auto" }}
                        />
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("passengerCitiesPage.empty")}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => {
                const checked = isRowSelected(row.id);
                const active = Number(row.status) === 1;
                return (
                  <TableRow key={row.id} hover selected={checked}>
                    {canDelete ? (
                      <TableCell>
                        <Checkbox checked={checked} onChange={() => toggleSelect(row.id)} />
                      </TableCell>
                    ) : null}
                    <TableCell>{row.nome || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.id_interno || "—"} />
                    </TableCell>
                    <TableCell>
                      {row.uf ? <Chip size="small" label={row.uf} /> : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.fonte || "manual"} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {canUpdate ? (
                          <Switch
                            size="small"
                            checked={active}
                            onChange={() => onToggleStatus?.(row)}
                          />
                        ) : null}
                        <Chip
                          size="small"
                          label={
                            active
                              ? t("table.statusActive")
                              : t("table.statusInactive")
                          }
                          color={active ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    {showActions ? (
                      <TableCell align="right">
                        {canUpdate ? (
                          <Tooltip title={t("passengerCitiesPage.edit")}>
                            <IconButton size="small" onClick={() => onEdit?.(row)}>
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {canDelete ? (
                          <Tooltip title={t("passengerCitiesPage.delete")}>
                            <IconButton size="small" onClick={() => onDelete?.(row)}>
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("passengerCitiesPage.rowsPerPage")}
          </Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((sz) => (
              <MenuItem key={sz} value={sz}>
                {sz}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage={t("passengerCitiesPage.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Paper>
  );
};

export default PassengerCitiesTable;
